
// // --- *** NEW & CRITICAL ***: Converts editable fields array to JSON Schema ---
// const fieldsToSchema = (fields, modelName = 'Output') => {
//     const convert = (fieldList) => {
//         const properties = {};
//         const required = [];

//         fieldList.forEach(field => {
//             if (!field.fieldName) return;
//             let schemaDef = { description: field.description };

//             const dataType = field.dataType.toLowerCase();

//             switch (dataType) {
//                 case 'string': case 'number': case 'boolean':
//                     schemaDef.type = dataType;
//                     break;
//                 case 'object':
//                     schemaDef.type = 'object';
//                     const nested = convert(field.children || []);
//                     schemaDef.properties = nested.properties;
//                     if (nested.required.length > 0) schemaDef.required = nested.required;
//                     break;
//                 case 'array':
//                     schemaDef.type = 'array';
//                     const itemType = field.arrayItemType?.toLowerCase() || 'string';
//                     if (itemType === 'object') {
//                         const nestedArray = convert(field.children || []);
//                         schemaDef.items = {
//                             type: 'object',
//                             properties: nestedArray.properties,
//                         };
//                         if (nestedArray.required.length > 0) {
//                             schemaDef.items.required = nestedArray.required;
//                         }
//                     } else {
//                         schemaDef.items = { type: itemType };
//                     }
//                     break;
//                 default:
//                     schemaDef.type = 'string';
//             }
//             properties[field.fieldName] = schemaDef;
//             if (field.required) required.push(field.fieldName);
//         });
//         return { properties, required };
//     };

//     const root = convert(fields);
//     return {
//         title: modelName,
//         type: 'object',
//         properties: root.properties,
//         ...(root.required.length > 0 && { required: root.required }),
//     };
// };

// export { fieldsToSchema };

// utils/schemaUtils.js
/**
 * Converts a JSON schema object into the array format used by the SchemaBuilder.
 * - Generates stable, path-based IDs essential for mapping and variable referencing.
 * - Capitalizes data types to match the UI components' expectations.
 *
 * @param {object} schema The JSON schema to convert.
 * @param {string} [prefix=''] A prefix for generating unique, stable IDs (e.g., 'input_node_1.').
 * @returns {Array} An array of field objects for the SchemaBuilder.
 */
export const schemaToFields = (schema, prefix = '') => {
    if (!schema || !schema.properties) return [];

    const convertProperties = (properties, requiredFields = [], currentPrefix) => {
        return Object.entries(properties).map(([key, value]) => {
            // Helper to ensure consistent capitalization
            const capitalize = (s) => (s && s.length > 0) ? s.charAt(0).toUpperCase() + s.slice(1) : '';

            const field = {
                // Predictable, path-based ID is crucial
                id: `${currentPrefix}${key}`,
                fieldName: key,

                // Capitalized to match SchemaBuilder's DATA_TYPES constant
                dataType: capitalize(value.type || 'string'),
                required: requiredFields.includes(key),
                ...(["string", "number", "boolean"].includes(value.type || 'string')
                    ? {
                        defaultValue: value.default !== undefined && value.default !== null
                            ? value.default
                            : (value.type === 'number' ? 0 : value.type === 'boolean' ? false : '')
                    }
                    : {}),
                description: value.description || '',
                children: [],
                arrayItemType: capitalize(value.items?.type || 'string'),
            };

            // Recurse for nested objects (for both 'object' type and 'array' of objects)
            const newPrefix = `${currentPrefix}${key}.`;
            if (field.dataType === 'Object' && value.properties) {
                field.children = convertProperties(value.properties, value.required || [], newPrefix);
            }
            else if (field.dataType === 'Array' && field.arrayItemType === 'Object' && value.items?.properties) {
                field.children = convertProperties(value.items.properties, value.items.required || [], newPrefix);
            }

            return field;
        });
    };

    return convertProperties(schema.properties, schema.required || [], prefix);
};


/**
 * Converts the array format from the SchemaBuilder back into a valid JSON schema.
 * - Converts capitalized data types back to lowercase for the schema standard.
 *
 * @param {Array} fields The array of field objects from the SchemaBuilder.
 * @param {string} [modelName='Output'] The title for the root of the schema.
 * @returns {object} A valid JSON schema object.
 */
export const fieldsToSchema = (fields, modelName = 'Output') => {
    const convert = (fieldList) => {
        const properties = {};
        const required = [];

        fieldList.forEach(field => {
            if (!field.fieldName) return;

            let schemaDef = {};
            if (field.description) {
                schemaDef.description = field.description;
            }

            const dataType = field.dataType.toLowerCase();

            switch (dataType) {
                case 'string':
                case 'number':
                case 'boolean':
                    schemaDef.type = dataType;
                    break;
                case 'object':
                    schemaDef.type = 'object';
                    const nested = convert(field.children || []);
                    schemaDef.properties = nested.properties;
                    if (nested.required.length > 0) schemaDef.required = nested.required;
                    break;
                case 'array':
                    schemaDef.type = 'array';
                    const itemType = field.arrayItemType?.toLowerCase() || 'string';
                    if (itemType === 'object') {
                        const nestedArray = convert(field.children || []);
                        schemaDef.items = {
                            type: 'object',
                            properties: nestedArray.properties,
                        };
                        if (nestedArray.required.length > 0) {
                            schemaDef.items.required = nestedArray.required;
                        }
                    } else {
                        schemaDef.items = { type: itemType };
                    }
                    break;
                default:
                    schemaDef.type = 'string'; // Fallback
            }
            // NEW: Add description to the schema definition
            if (field.description) {
                schemaDef.description = field.description;
            }
            // ⭐ NEW: Add the 'default' property to the schema if it exists
            if (field.defaultValue !== null && ['String', 'Number', 'Boolean'].includes(field.dataType)) {
                schemaDef.default = field.defaultValue;
            }

            properties[field.fieldName] = schemaDef;
            if (field.required) required.push(field.fieldName);
        });
        return { properties, required };
    };

    const root = convert(fields);
    return {
        title: modelName,
        type: 'object',
        properties: root.properties,
        ...(root.required.length > 0 && { required: root.required }),
    };
};



/**
 * Recursively generates the initial data object from the schema fields.
 * @param {Array} fields - The array of field objects from the SchemaBuilder.
 * @returns {Object} The initial data object with default values.
 */
export const generateInitialStateData = (fields) => {
    const data = {};
    for (const field of fields) {
        if (!field.fieldName) continue;

        if (field.dataType === 'Object' && field.children?.length > 0) {
            data[field.fieldName] = generateInitialStateData(field.children);
        } else if (field.dataType === 'Array') {
            // For arrays of objects, the default is an empty array.
            // For simple arrays, also default to an empty array.
            data[field.fieldName] = [];
        } else {
            // Use the defaultValue set by the user, or the default for the type.
            data[field.fieldName] = field.defaultValue;
        }
    }
    return data;
};


// Helper to flatten schemas, now with a filter option
export const flattenSchemaToFields = (rootFields, filterFn = () => true) => {
    const fields = [];
    const recurse = (nodes, pathPrefix, displayPrefix) => {
        for (const node of nodes) {
            const newPath = pathPrefix ? `${pathPrefix}.${node.fieldName}` : node.id;
            const newDisplay = displayPrefix ? `${displayPrefix}.${node.fieldName}` : node.fieldName;

            const fieldData = { ...node, path: newPath, display: newDisplay };
            if (filterFn(fieldData)) {
                fields.push(fieldData);
            }

            if (node.children && node.children.length > 0) {
                recurse(node.children, newPath, newDisplay);
            }
        }
    };
    recurse(rootFields, '', '');
    return fields;
};
